import { useCallback, useState } from "react";
import { saveAs } from "file-saver";
import { getAllEmissionFactors, getAllKpis } from "../kpiGridApi";
import { factorsByKpiFromWire, normalizeRow } from "./helpers";
import { buildWorkbook } from "./excel/buildWorkbook";
import { readWorkbook } from "./excel/readWorkbook";
import { planImport, referencedKpiIds } from "./excel/planImport";
import { applyImport } from "./excel/applyImport";

const loadExcel = () => import("exceljs").then((mod) => mod.default || mod);

const attachFactors = (rows, factorsByKpi) =>
  rows.map((row) => ({ ...row, factors: factorsByKpi[String(row.questionId)] || [] }));

const fileNameFor = (template, financialYearLabel) => {
  const year = String(financialYearLabel || "").replace(/[^\w-]+/g, "_");
  return template ? "KPI_template.xlsx" : `KPIs_${year || "export"}.xlsx`;
};

/**
 * The Excel round trip for the KPI grid.
 *
 * - `download(template)` writes the blank template, or every KPI matching the
 *   grid's filters (all pages) with their emission factors. The workbook is
 *   one financial year's, so the page only offers it when one year is showing.
 * - `preview(file)` reads an upload and plans it against the whole financial
 *   year, inactive KPIs included, so IDs and titles resolve wherever they sit.
 * - `apply()` saves the plan; `onApplied` lets the grid refresh.
 *
 * @param {{ catalogue: object, financialYearId: string, financialYearLabel: string, filters: object, onApplied: () => void }} args
 */
export function useExcelTransfer({ catalogue, financialYearId, financialYearLabel, filters, onApplied }) {
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [plan, setPlan] = useState(null);
  const [result, setResult] = useState(null);

  const run = useCallback(async (label, task) => {
    setBusy(label);
    setError("");
    try {
      return await task();
    } catch (e) {
      setError(e?.message || "Something went wrong");
      return null;
    } finally {
      setBusy("");
    }
  }, []);

  const download = useCallback(
    (template) =>
      run(template ? "Preparing the template…" : "Collecting KPIs…", async () => {
        const ExcelJS = await loadExcel();
        let rows = [];
        if (!template) {
          rows = (await getAllKpis({ ...filters, financialYearIds: [financialYearId] })).map(normalizeRow);
          const factors = await getAllEmissionFactors(rows.map((row) => row.questionId));
          rows = attachFactors(rows, factorsByKpiFromWire(factors));
        }
        const workbook = buildWorkbook(ExcelJS, { rows, catalogue, financialYearLabel });
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(
          new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
          fileNameFor(template, financialYearLabel),
        );
      }),
    [run, filters, financialYearId, catalogue, financialYearLabel],
  );

  const preview = useCallback(
    (file) =>
      run("Reading the file…", async () => {
        setResult(null);
        const ExcelJS = await loadExcel();
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(await file.arrayBuffer());
        const read = readWorkbook(workbook);

        const existing = (await getAllKpis({ financialYearIds: [financialYearId], includeInactive: true })).map(normalizeRow);
        const factorIds = referencedKpiIds(read, existing);
        const factorsByKpi = factorIds.length ? factorsByKpiFromWire(await getAllEmissionFactors(factorIds)) : {};

        setPlan({ ...planImport({ read, existing, factorsByKpi, catalogue }), fileName: file.name });
      }),
    [run, financialYearId, catalogue],
  );

  const apply = useCallback(
    () =>
      run("Saving…", async () => {
        try {
          setResult(await applyImport(plan, { financialYearId, onProgress: setBusy }));
        } finally {

          setPlan(null);
          await onApplied();
        }
      }),
    [run, plan, financialYearId, onApplied],
  );

  const close = useCallback(() => {
    setPlan(null);
    setResult(null);
    setError("");
  }, []);

  return { busy, error, plan, result, download, preview, apply, close };
}
